import { useState } from "react";
import {
  ConfirmDeleteModal,
  EntitySlideout,
  saveErrorReason,
  saveErrorToast,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useLookupsList } from "./api/useLookupsList";
import { useSelectOptionQuestions } from "./api/useSelectOptionQuestions";
import { useLookupMutations } from "./api/useLookupMutations";
import { LookupsList } from "./LookupsList";
import { LookupForm } from "./LookupForm";
import {
  buildLookupTable,
  emptyLookupForm,
  type Lookup,
  type LookupFormValues,
} from "./types";

const isValidLookup = (v: LookupFormValues): boolean =>
  v.name.trim() !== "" && v.index1 !== "";

export interface LookupsTabProps {
  questionnaireId: string;
}

const toFormValues = (l: Lookup): LookupFormValues => ({
  name: l.name,
  valueType: l.valueType,
  index1: l.index1,
  index2: l.index2,
  lookups: l.lookups,
});

export const LookupsTab = ({
  questionnaireId,
}: LookupsTabProps): JSX.Element => {
  const lookups = useLookupsList(questionnaireId);
  const { questions } = useSelectOptionQuestions(questionnaireId);
  const mutations = useLookupMutations(questionnaireId);
  const sel = useBatchSelection();

  const [view, setView] = useState<
    { mode: "list" } | { mode: "form"; editing?: Lookup }
  >({ mode: "list" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const questionName = (slug: string) =>
    questions.find((q) => q.slug === slug)?.name ?? slug;

  const saveLookup = async (values: LookupFormValues) => {
    const idx1 = questions.find((q) => q.slug === values.index1);
    const idx2 = values.index2
      ? questions.find((q) => q.slug === values.index2)
      : undefined;
    const cleaned: LookupFormValues = {
      ...values,
      lookups: buildLookupTable(
        values.lookups,
        idx1?.options ?? [],
        idx2 ? idx2.options : null
      ),
    };
    try {
      if (view.mode === "form" && view.editing) {
        await mutations.update(view.editing.id, cleaned);
      } else {
        await mutations.create(cleaned);
      }
      setView({ mode: "list" });
      lookups.refetch();
    } catch (err) {
      setToast(saveErrorToast(err, "lookup"));
    }
  };

  const confirmBatchDelete = async () => {
    setConfirmDelete(false);
    const ids = [...sel.selectedIds];
    const failures: BatchFailure[] = [];
    for (const id of ids) {
      try {
        await mutations.remove(id);
      } catch (err) {
        failures.push({ reason: saveErrorReason(err) });
      }
    }
    if (failures.length > 0) {
      setToast({
        variant: "danger",
        text: withFailureReasons(
          `Failed to delete ${failures.length} of ${ids.length} lookup(s).`,
          failures
        ),
      });
    }
    sel.clear();
    lookups.refetch();
  };

  if (lookups.isLoading) return <ScopeStackSpinner />;

  return (
    <div className="lookups-tab">
      {questions.length === 0 ? (
        <div className="text-muted p-3">
          Add checkbox/select questions before creating lookups.
        </div>
      ) : (
        <LookupsList
          rows={lookups.rows}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onAdd={() => setView({ mode: "form" })}
          onEdit={(l) => setView({ mode: "form", editing: l })}
          onBatchDelete={() => setConfirmDelete(true)}
          questionName={questionName}
        />
      )}

      <EntitySlideout
        isOpen={view.mode === "form"}
        initialValues={
          view.mode === "form" && view.editing
            ? toFormValues(view.editing)
            : emptyLookupForm
        }
        title={
          view.mode === "form" && view.editing ? "Edit Lookup" : "Add Lookup"
        }
        isValid={isValidLookup}
        onClose={() => setView({ mode: "list" })}
        onSave={saveLookup}
        className="wideSlideout"
      >
        {(values, onChange) => (
          <LookupForm
            mode={view.mode === "form" && view.editing ? "edit" : "create"}
            values={values}
            onChange={onChange}
            slug={(view.mode === "form" && view.editing?.slug) || ""}
            questions={questions}
          />
        )}
      </EntitySlideout>

      <ConfirmDeleteModal
        open={confirmDelete}
        title="Delete lookups?"
        message={`Delete ${sel.selectedIds.length} selected lookup(s)?`}
        confirmLabel="Delete"
        onConfirm={confirmBatchDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default LookupsTab;
